import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { Project } from '@app/models/time-tracking/project.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TimeTrackingMenuService } from '@app/modules/time-tracking/services';
import { ProjectsIndexTable } from '@app/modules/time-tracking/services/tables/projects-index/projects-index.tables';
import { AuthService, NotifyService } from '@app/services';

@Component({
    templateUrl: './time-tracking-index.template.html',
})
export class TimeTrackingIndexView {
    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmationDialogComponent;
    @ViewChild('confirmArchiveModal') confirmArchiveModal!: ConfirmationDialogComponent;
    @ViewChild('archiveInsteadModal') archiveInsteadModal!: ConfirmationDialogComponent;
    isLoading = true;
    isAdmin = false;

    projectToArchive: Project | null = null;

    constructor(
        public table: ProjectsIndexTable,
        public menuService: TimeTrackingMenuService,
        private route: ActivatedRoute,
        private router: Router,
        private notifyService: NotifyService,
        private authService: AuthService
    ) {
        this.isAdmin = this.authService.can(Platform.timeTracking.permission.view);
    }

    async ngOnInit(): Promise<void> {
        this.table.reload();
        this.isLoading = false;
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;

        const project = data as Project;

        switch (menuItem.event) {
            case 'edit':
                this.onEdit(project);
                break;
            case 'delete':
                this.deleteProject(project);
                break;
            case 'archive':
                this.archiveProject(project);
                break;
            case 'markAsActive':
                this.markAsActive(project);
                break;
        }
    }

    onEdit(project: Project): void {
        this.router.navigate([project.id, 'edit'], { relativeTo: this.route });
    }

    async deleteProject(project: Project): Promise<void> {
        // we don't allow you to delete an archived project
        if (project.hasTimesheetEntries) {
            const justClose = await this.archiveInsteadModal.open();
            if (!justClose) {
                this.archiveProject(project);
            }
            return;
        }

        const shouldDelete = await this.confirmDeleteModal.open();
        if (!shouldDelete) {
            return;
        }

        try {
            await project.delete();
            this.table.reload();
        } catch (e) {
            this.notifyService.error('time-tracking.deleteError');
        }
    }

    async archiveProject(project: Project): Promise<void> {
        this.projectToArchive = project;
        const shouldArchive = await this.confirmArchiveModal.open();
        this.projectToArchive = null;
        if (!shouldArchive) {
            return;
        }

        this.isLoading = true;

        try {
            project.isArchived = true;
            await project.save();
            this.table.reload();
        } catch (error) {
            this.notifyService.error('time-tracking.archiveError');
        }

        this.isLoading = false;
    }

    async markAsActive(project: Project): Promise<void> {
        this.isLoading = true;

        try {
            project.isArchived = false;
            await project.save();
            this.table.reload();
        } catch (error) {
            this.notifyService.error('time-tracking.markAsActiveError');
        }

        this.isLoading = false;
    }
}
